import { Box, Button } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import TicketTypeFields from "../TicketTypeFields";
import FacilityAndTicketTypeSelection from "./FacilityAndTicketTypeSelection";
import Iconify from "../../../../../../reusable-components/iconify";
import ProgressBar from "../../../../../../reusable-components/progress-bar/ProgressBar";
import MobileNewTicketView from "./MobileNewTicketView";
import { setIsAddingNewTicket } from "../../../../../../redux/slices/tickets";
import { changeActiveGroup, getHeaderGroups, getPossibleUsers, setInitialTicketHtmlHelper, setTicketTypeFieldGroupsHelper } from "../TicketHelper";

export default function MobileAddNewTicket() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { ticketTypes } = useSelector((state) => state.tickets.data);

  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedTicketCategory, setSelectedTicketCategory] = useState(null);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [ticketTypeFieldGroups, setTicketTypeFieldGroups] = useState([]);
  const [isTicketMessagesEditorOpen, setIsTicketMessagesEditorOpen] = useState(false);
  const [isAutoTicketMessagesEditorOpen, setIsAutoTicketMessagesEditorOpen] = useState(false);
  const [possibleUsers, setPossibleUsers] = useState(null);
  const [ticketHtml, setTicketHtml] = useState(null);
  const [ticketSubject, setTicketSubject] = useState('');
  const [newUsers, setNewUsers] = useState(user ? [{ value: `${user.firstName} ${user.lastName}`, id: user.email }] : []);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [confirmedCategoryFacilityAndType, setConfirmedCategoryFacilityAndType] = useState(false);

  useEffect(() => {
    getPossibleUsers(setPossibleUsers, selectedFacility?.id && selectedTicketCategory?.value, selectedFacility, selectedTicketCategory, selectedTicketType);
  }, [selectedFacility?.id, selectedTicketCategory?.value]);

  useEffect(() => {
    setTicketTypeFieldGroupsHelper(selectedTicketType, setTicketTypeFieldGroups, confirmedCategoryFacilityAndType,
      () => setInitialTicketHtmlHelper(setIsTicketMessagesEditorOpen, selectedTicketType, ticketTypeFieldGroups, setTicketSubject, setNewUsers, setTicketHtml, user));
  }, [selectedTicketType, isAutoTicketMessagesEditorOpen])

  const handleBackClick = () => {
    if ((!ticketTypeFieldGroups?.length || isAutoTicketMessagesEditorOpen && !ticketTypeFieldGroups?.some(group => group.complete))) dispatch(setIsAddingNewTicket(false));
    else {
      changeActiveGroupHelper('back');
    }
  }

  useEffect(() => {
    if(ticketTypeFieldGroups.length === 0 && confirmedCategoryFacilityAndType) {
      setConfirmedCategoryFacilityAndType(false);
    }
  }, [ticketTypeFieldGroups])

  const changeActiveGroupHelper = (direction = "next") => changeActiveGroup(setTicketTypeFieldGroups,
    () => setInitialTicketHtmlHelper(setIsTicketMessagesEditorOpen, selectedTicketType, ticketTypeFieldGroups, setTicketSubject, setNewUsers, setTicketHtml, user), direction);

  const headerGroups = useMemo(() => getHeaderGroups(ticketTypeFieldGroups), [ticketTypeFieldGroups]);

  useEffect(() => () => dispatch(setIsAddingNewTicket(false)), []);

  useEffect(() => {   
    setTicketTypeFieldGroupsHelper(selectedTicketType, setTicketTypeFieldGroups, confirmedCategoryFacilityAndType,
      () => setInitialTicketHtmlHelper(setIsTicketMessagesEditorOpen, selectedTicketType, ticketTypeFieldGroups, setTicketSubject, setNewUsers, setTicketHtml, user, true));      
  }, [selectedTicketType, confirmedCategoryFacilityAndType]);

  return !isTicketMessagesEditorOpen ? (
    <Box
      sx={{
        height: '98%',
        borderRadius: 2,
        display: 'flex',
        mt: 1,
        flexDirection: 'column',
        p: 2,
        overflowY: "auto"
      }}
      style={{ backgroundColor: 'white' }}
    >
      <Button
        onClick={handleBackClick}
        startIcon={<Iconify icon="mdi:chevron-left" />}
        sx={{ mb: 1, alignSelf: 'flex-start' }}
        size="small"
        variant="text"
      >
        Back {!ticketTypeFieldGroups?.length ? `to Tickets` : ''}
      </Button>
      <ProgressBar values={[100, confirmedCategoryFacilityAndType || (ticketTypes && filteredTypes.length === 0) ? 100 : 0, ...headerGroups]} />
      {!confirmedCategoryFacilityAndType ? <FacilityAndTicketTypeSelection
        setConfirmedCategoryFacilityAndType={setConfirmedCategoryFacilityAndType}
        setTicketTypeFieldGroups={setTicketTypeFieldGroups}
        selectedFacility={selectedFacility}
        setSelectedFacility={setSelectedFacility}
        selectedTicketCategory={selectedTicketCategory}
        setSelectedTicketCategory={setSelectedTicketCategory}
        setSelectedTicketType={setSelectedTicketType}
        selectedTicketType={selectedTicketType}
        filteredCategories={filteredCategories}
        setFilteredCategories={setFilteredCategories}
        filteredTypes={filteredTypes}
        setFilteredTypes={setFilteredTypes}
        setIsAutoTicketMessagesEditorOpen={setIsAutoTicketMessagesEditorOpen}
        isAutoTicketMessagesEditorOpen={isAutoTicketMessagesEditorOpen}
      /> : <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', mt: 2, overflow: 'auto', flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <TicketTypeFields
            ticketTypeFieldGroups={ticketTypeFieldGroups.filter(group => group.active)}
            setTicketTypeFieldGroups={setTicketTypeFieldGroups}
            facility={selectedFacility}
          />
        </Box>
        <Button
          onClick={() => changeActiveGroupHelper('next')}
          variant="contained"
          sx={{ width: 'fit-content', alignSelf: 'flex-end', mt: 2 }}
          disabled={ticketTypeFieldGroups.filter(group => group.active)?.some(group => !group.repeatedGroupId && group.fields?.some(field => field.required && !field.value))}
        >
          Next <Iconify icon="mdi:chevron-right" />
        </Button>
      </Box>}
    </Box>) :
    <MobileNewTicketView
      possibleUsers={possibleUsers}
      ticketTypeFieldGroups={ticketTypeFieldGroups}
      ticketHtml={ticketHtml}
      ticketSubject={ticketSubject}
      setTicketSubject={setTicketSubject}
      newUsers={newUsers}
      setNewUsers={setNewUsers}
      selectedFacility={selectedFacility}
      selectedTicketType={selectedTicketType}
      setIsTicketMessagesEditorOpen={setIsTicketMessagesEditorOpen}
      selectedTicketCategory={selectedTicketCategory}
      handleBackClick={handleBackClick}
    />
}